import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Settings, LogOut, MessageSquareWarning } from 'lucide-react';
import { openBugReporter } from '@/lib/bug-reporter';
import { useLocalePrefix } from '@/lib/hooks/useLocaleNavigate';
import { useAuth } from '@/lib/auth/auth-context';
import { UserType, USER_TYPE_LABELS } from '@/lib/auth/permissions';
import { useGating } from '@/lib/auth/use-gating';
import { LanguageSelector } from './LanguageSelector';
import { cn } from '@/lib/utils';

/**
 * Angela's grey strip (User / Audit log / setting / sign out), moved from the
 * sidebar footer to the top-right of the header — see
 * docs/specs/nav-ia-restructure/feature-map.md §3.1.
 *
 * Gating is per item, not per menu: she asked for the HQ items to be hidden
 * from outlet staff, but Sign out and Language must stay reachable for
 * everyone, so outlet staff still open the menu and see just those two.
 *
 * Not present yet, and deliberately absent rather than shown disabled:
 *   - Roles    → Spec A (RBAC), splits from Users
 *   - Audit log → not built
 */
export function UserMenu({ placement = 'top' }: { placement?: 'top' | 'rail' } = {}) {
  const { t } = useTranslation('nav');
  const { t: tAuth } = useTranslation('auth');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const prefix = useLocalePrefix();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const userType = (user?.userType || 'RECEPTIONIST') as UserType;
  const userEmail = user?.email || '';
  // BUG-883 (Angela): "Is the current display name on the top right following our
  // email add? Can we change to first and last name instead?" It was
  // userEmail.split('@')[0]. The name is now resolved server-side from the linked
  // staff record, so this shows the person's actual name.
  const userName = user?.name || userEmail.split('@')[0];
  const userTypeLabel = USER_TYPE_LABELS[userType] || userType;

  // ENG-YAKSON-080 (A7): gate the HQ items on the backend's resolved
  // permissions (falling back to the legacy matrix while they load / on
  // failure). Settings mirrors the legacy "everyone can access" (dashboard).
  // (User management moved out of here into the HR sidebar section — Angela 25 Jul.)
  const { canFeature } = useGating();
  const canSeeSettings = canFeature('dashboard');

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    function onEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, []);

  const handleLogout = () => {
    logout();
    navigate(prefix + '/login');
  };

  const itemClasses =
    'flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="menu"
        data-testid="user-menu-trigger"
        aria-expanded={isOpen}
        title={userEmail}
        className={cn(
          placement === 'rail'
            ? 'flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#FFE4E8] text-sm font-extrabold text-[#BE123C] transition-colors hover:bg-white'
            : 'flex h-8 w-8 items-center justify-center rounded-full bg-[#FF8189]/10 text-[#FF8189] text-sm font-medium transition-colors hover:bg-[#FF8189]/20',
          isOpen && placement !== 'rail' && 'bg-[#FF8189]/20'
        )}
      >
        {userName.charAt(0).toUpperCase() || '?'}
      </button>

      {isOpen && (
        <div
          role="menu"
          data-testid="user-menu"
          className={cn(
            'absolute z-50 w-56 rounded-lg border border-border bg-white py-1 shadow-lg',
            // In the rail (bottom left) the menu opens to the right, upwards.
            placement === 'rail' ? 'bottom-0 left-full ml-3' : 'right-0 top-full mt-2',
          )}
        >
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm text-foreground">{userName}</p>
            <p className="truncate text-xs text-muted-foreground">{userTypeLabel}</p>
          </div>

          {canSeeSettings && (
            <Link to={prefix + '/settings'} onClick={() => setIsOpen(false)} className={itemClasses} role="menuitem" data-testid="user-menu-settings">
              <Settings className="h-4 w-4" />
              <span>{t('settings')}</span>
            </Link>
          )}

          {/* Report a bug — moved here from the sidebar rail (Angela, tracker
              item 13). Ungated on purpose: anyone who can see a fault should be
              able to report it, so this sits outside `canSeeSettings` alongside
              Sign out and Language, which are also reachable by outlet staff.
              The SDK is initialised in Sidebar.tsx, which already knows the
              signed-in user; this only opens it. */}
          <button
            onClick={() => {
              setIsOpen(false);
              openBugReporter();
            }}
            role="menuitem"
            data-testid="user-menu-report-bug"
            className={itemClasses}
          >
            <MessageSquareWarning className="h-4 w-4" />
            <span>{t('reportBug')}</span>
          </button>

          {/* Language moved out of the header bar to keep it uncluttered. */}
          <div className="border-t border-border px-3 py-2">
            <LanguageSelector />
          </div>

          <div className="border-t border-border pt-1">
            <button
              onClick={handleLogout}
              role="menuitem"
              data-testid="user-menu-logout"
              className="flex w-full items-center gap-3 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              <span>{tAuth('logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
